/**
 * Size unit conversion, shared by the PDP size selector and both cart surfaces.
 *
 * The unit a size is shown in is per-line, not per-product: `_size_unit` is written as a line
 * property at add-to-cart from whichever tab the selector was on, so a line added in EU keeps
 * showing and offering EU even where the product's `x-sizing` tag says US. Lines added anywhere
 * that does not write the property (PLP quick add, upsell, wishlist) fall back to that tag.
 *
 * size-selector.js reads `sizeUnitValue` from here too, so a cart line's label can never disagree
 * with the pill the customer pressed on the product page.
 */

/** The unit tab values, matching `sizeTypes` in size-selector.js. */
export const SIZE_UNITS = ['UK', 'US', 'EURO', 'CM']
export const DEFAULT_SIZE_UNIT = 'US'

/** The variant field each unit reads. */
const UNIT_FIELDS = {
  UK: 'uk',
  US: 'us',
  EURO: 'eu',
  CM: 'cm'
}

/**
 * How a unit is shown to a shopper. AU and UK footwear run the same scale here, so the UK tab
 * reads "AU/UK" wherever a unit is named. Unit *values* are untouched — they key the utopia
 * metafields, the `x-sizing` tag and the `_size_unit` line property, and a rename would break
 * variant-title matching.
 *
 * Keyed on both `EURO` (this module, size-selector) and `EU` (SearchSpring's FIELD_MAPPING).
 */
const UNIT_LABELS = {
  UK: 'AU/UK',
  US: 'US',
  EURO: 'EU',
  EU: 'EU',
  CM: 'CM'
}

/**
 * @param {string} unit A unit value, in either key space.
 * @returns {string} The label, or the input unchanged when it is not a unit.
 */
export const sizeUnitLabel = (unit) => {
  const key = String(unit ?? '').trim().toUpperCase()
  // An absent unit falls back to the default label; an unrecognised one is passed through.
  return UNIT_LABELS[key] ?? (unit || UNIT_LABELS[DEFAULT_SIZE_UNIT])
}

const normaliseUnit = (unit) => {
  const upper = String(unit ?? '').trim().toUpperCase()
  return SIZE_UNITS.includes(upper) ? upper : null
}

/**
 * A variant's size in a given unit.
 *
 * Falls back to the variant title rather than an empty string: a product whose variants carry
 * only some of the four unit metafields would otherwise render blank options.
 *
 * @param {object} variant Parsed variant row: { title, uk, us, eu, cm }.
 * @param {string} [unit] One of SIZE_UNITS. Anything else is treated as the default.
 * @returns {string|null}
 */
export const sizeUnitValue = (variant, unit) => {
  if (!variant) return null
  const field = UNIT_FIELDS[normaliseUnit(unit) ?? DEFAULT_SIZE_UNIT]
  const value = variant[field]
  return value === undefined || value === null || value === '' ? variant.title ?? variant.name ?? null : String(value)
}

/**
 * A variant's size with its unit spelled out, for a cart control where nothing else says which
 * scale the number is on — `5 US`.
 *
 * The suffix is only added when the unit's own metafield supplied the value; a variant missing it
 * falls back to the variant title, and labelling that with a unit would assert a conversion that
 * never happened.
 *
 * @param {object} variant Parsed variant row: { title, uk, us, eu, cm }.
 * @param {string} [unit] One of SIZE_UNITS. Anything else is treated as the default.
 * @returns {string|null}
 */
export const sizeUnitDisplay = (variant, unit) => {
  const value = sizeUnitValue(variant, unit)
  if (value === null) return null
  const field = UNIT_FIELDS[normaliseUnit(unit) ?? DEFAULT_SIZE_UNIT]
  const raw = variant?.[field]
  if (raw === undefined || raw === null || raw === '') return value
  return `${value} ${sizeUnitLabel(unit)}`
}

/**
 * One line property, whichever shape Liquid produced.
 *
 * `item | json` renders properties as an object, but `item.properties | json` renders the same
 * data as an array of [key, value] pairs — and the cart page hit exactly that, silently losing
 * the unit and falling back to the product default. Both shapes are read here so a call site
 * cannot reintroduce it.
 */
const lineProperty = (item, name) => {
  const properties = item?.properties
  if (!properties) return undefined
  if (Array.isArray(properties)) return properties.find((pair) => pair?.[0] === name)?.[1]
  return properties[name]
}

/**
 * A line's properties as a plain object, whichever shape Liquid produced — the form
 * /cart/add.js expects when a swap re-adds the line.
 *
 * @param {object} item Cart line item.
 * @returns {object}
 */
export const lineProperties = (item) => {
  const properties = item?.properties
  if (!properties) return {}
  if (!Array.isArray(properties)) return properties
  return Object.fromEntries(properties.filter((pair) => Array.isArray(pair) && pair.length === 2))
}

/**
 * The unit a cart line should display in: the unit it was added in, else the product default,
 * else US. An unrecognised value at either step is skipped rather than shown.
 *
 * @param {object} item Cart line item as emitted by cart-json.liquid.
 * @returns {string}
 */
export const resolveLineSizeUnit = (item) =>
  normaliseUnit(lineProperty(item, '_size_unit')) ?? normaliseUnit(item?.sizing_tag) ?? DEFAULT_SIZE_UNIT

/**
 * The size options for a line's dropdown, in variant order.
 *
 * Restricted to the line variant's width, since swapping width is a different decision than
 * swapping size and the PDP keeps them on separate rows. Sold-out sizes are offered but disabled,
 * so a customer can see their size exists rather than assuming it was never made — except the
 * line's own variant, which stays selectable so the control always has a valid value.
 *
 * @param {object} item Cart line item as emitted by cart-json.liquid.
 * @param {string} unit Resolved display unit.
 * @returns {{id: string, label: string, disabled: boolean, selected: boolean}[]}
 */
export const buildLineSizeOptions = (item, unit) => {
  const variants = Array.isArray(item?.size_variants) ? item.size_variants : []
  if (!variants.length) return []

  const isSelected = (variant) => String(variant.id) === String(item?.variant_id)
  const lineWidth = variants.find(isSelected)?.width

  return variants
    .filter((variant) => !lineWidth || variant.width === lineWidth)
    .map((variant) => ({
      id: String(variant.id),
      label: sizeUnitDisplay(variant, unit),
      disabled: !variant.available && !isSelected(variant),
      selected: isSelected(variant)
    }))
}
